import { Box, Flex, Heading, Text, VStack } from "@chakra-ui/react";
import { useCallback, useState } from "react";
import { apiCognito } from "../../api/api";
import { capture } from "../../shared/capture";
import { useAppContext } from "../contexts/app";
import { AppStateActionType, Keys } from "../contexts/app/reducer";
import Key from "../key";
import { Actions, Analytics, Features } from "../shared/analytics";
import { Button } from "../shared/Button";
import { useIsUserInSandbox } from "../shared/useIsUserInSandbox";

export default function ApiKey() {
  const { dispatch, state } = useAppContext();

  const { userIsSandbox } = useIsUserInSandbox(state);

  const [isGeneratingSecretKey, setIsGeneratingSecretKey] = useState(false);
  const [isGeneratingClientKey, setIsGeneratingClientKey] = useState(false);

  const updateKey = useCallback(
    (keyName: keyof Keys, apiKey: string) => {
      dispatch({
        type: AppStateActionType.update,
        newState: {
          keys: {
            ...state.keys,
            [keyName]: apiKey,
          },
        },
      });
    },
    [state, dispatch]
  );

  async function generateKey(
    type: keyof Keys,
    setIsGenerating: (bool: boolean) => void,
    params?: { clientKey: boolean }
  ) {
    setIsGenerating(true);
    try {
      const resp = await apiCognito.post("/generateKey", null, {
        headers: { "Content-Type": "application/json" },
        params,
      });
      Analytics.emit(Actions.generate, Features[type]);
      updateKey(type, resp.data.key);
    } catch (error) {
      capture.error(error, { extra: { context: `apiKey.generate` } });
    }
    setIsGenerating(false);
  }

  async function revokeKey(
    type: keyof Keys,
    setIsGenerating: (bool: boolean) => void,
    params?: { clientKey: boolean }
  ) {
    setIsGenerating(true);
    try {
      const resp = await apiCognito.post("/revokeKey", null, {
        headers: { "Content-Type": "application/json" },
        params,
      });

      if (!resp.data.success) {
        throw new Error("Failed to revoke API key");
      }
      Analytics.emit(Actions.revoke, Features[type]);
      updateKey(type, "");
    } catch (error) {
      capture.error(error, { extra: { context: `apiKey.revoke` } });
    }
    setIsGenerating(false);
  }

  const isSubscriptionActive =
    userIsSandbox || (!userIsSandbox && state.customer?.subscriptionStatus === "active");

  return (
    <Box pb={10}>
      <VStack align="stretch">
        <Heading>API Keys</Heading>
        <KeysContainer
          title="Secret Key"
          apiKey={state.keys?.apiKey}
          isGenerating={isGeneratingSecretKey}
          generateKey={() => generateKey("apiKey", setIsGeneratingSecretKey)}
          revokeKey={() => revokeKey("apiKey", setIsGeneratingSecretKey)}
          isActive={isSubscriptionActive}
        />
        <KeysContainer
          title="Client Key"
          apiKey={state.keys?.clientApiKey}
          isGenerating={isGeneratingClientKey}
          generateKey={() =>
            generateKey("clientApiKey", setIsGeneratingClientKey, { clientKey: true })
          }
          revokeKey={() => revokeKey("clientApiKey", setIsGeneratingClientKey, { clientKey: true })}
          isActive={isSubscriptionActive}
        />
      </VStack>
    </Box>
  );
}

type KeysProps = {
  title: string;
  apiKey: string | undefined;
  isGenerating: boolean;
  generateKey: () => void;
  revokeKey: () => void;
  isActive: boolean;
};

const KeysContainer = ({
  title,
  apiKey,
  isGenerating,
  generateKey,
  revokeKey,
  isActive,
}: KeysProps) => {
  const hasKey = apiKey && apiKey.length;

  if (hasKey) {
    return (
      <Flex direction={"column"}>
        <Key value={apiKey} revokeKey={revokeKey} children={title} />
      </Flex>
    );
  }

  if (isActive) {
    return (
      <Button
        isLoading={isGenerating}
        loadingText={`Generating ${title}`}
        size="lg"
        maxWidth={400}
        onClick={generateKey}
      >
        Generate {title}
      </Button>
    );
  }

  return <Text>Active subscription is required to generate your {title}.</Text>;
};
